import React, { useState, useRef } from 'react';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Monitor, 
  Smartphone, 
  Tablet,
  Eye,
  Download,
  Share2,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { Button } from './ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.tsx';
import { cn } from '../lib/utils.ts';
import { Storyboard, StoryboardStep } from '../types/index.ts';
import { logger } from '../lib/logger.ts';

interface PreviewModeProps {
  storyboard: Storyboard;
  onExport: (format: 'json' | 'pdf' | 'html') => void;
  className?: string;
}

type DeviceType = 'desktop' | 'tablet' | 'mobile';

const deviceSizes = {
  desktop: { width: '100%', height: '600px' },
  tablet: { width: '768px', height: '1024px' },
  mobile: { width: '375px', height: '667px' }
};

const SimpiStepPreview: React.FC<{
  step: StoryboardStep;
  isActive: boolean;
  onPlay: () => void;
  onPause: () => void;
  isPlaying: boolean;
  isMuted: boolean;
  onToggleMute: () => void;
}> = ({ step, isActive, onPlay, onPause, isPlaying, isMuted, onToggleMute }) => {
  return (
    <div className={cn(
      'relative bg-white rounded-lg border-2 transition-all duration-300',
      isActive ? 'border-primary shadow-lg' : 'border-gray-200'
    )}>
      {/* Mock Visual Area */}
      <div className="aspect-video bg-gradient-to-br from-blue-50 to-purple-50 rounded-t-lg flex items-center justify-center">
        <div className="text-center p-6">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Eye className="h-8 w-8 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground italic">
            {step.visualSuggestion}
          </p>
        </div>
      </div>

      {/* Step Content */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
              {step.order}
            </div>
            <h3 className="font-semibold text-lg">{step.onscreenText}</h3>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleMute}
              className="h-8 w-8 p-0"
            >
              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={isPlaying ? onPause : onPlay}
              className="h-8 w-8 p-0"
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* TTS Text */}
        <div className="bg-gray-50 rounded-lg p-3 mb-3">
          <p className="text-sm text-gray-700 leading-relaxed">
            {step.ttsText}
          </p>
        </div>

        {/* Source Reference */}
        <div className="text-xs text-muted-foreground">
          <span className="font-medium">Source:</span> {step.sourceDocReference.section} 
          <span className="mx-2">•</span>
          <span>Page {step.sourceDocReference.page}</span>
          <span className="mx-2">•</span>
          <span>{Math.round(step.sourceDocReference.confidence * 100)}% confidence</span>
        </div>
      </div>

      {/* Progress Bar */}
      {isPlaying && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-lg overflow-hidden">
          <div className="h-full bg-primary animate-pulse"></div>
        </div>
      )}
    </div>
  );
};

export const PreviewMode: React.FC<PreviewModeProps> = ({
  storyboard,
  onExport,
  className
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [deviceType, setDeviceType] = useState<DeviceType>('desktop');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  const currentStep = storyboard.steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === storyboard.steps.length - 1;

  const handlePlayStep = () => {
    setIsPlaying(true);
    // Simulate TTS playback
    setTimeout(() => {
      setIsPlaying(false);
    }, 3000);
  };

  const handlePauseStep = () => {
    setIsPlaying(false);
  };

  const handleNextStep = () => {
    if (!isLastStep) {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const handlePrevStep = () => {
    if (!isFirstStep) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const handleRestart = () => {
    setCurrentStepIndex(0);
    setIsPlaying(false);
  };

  const handleDeviceChange = (device: DeviceType) => {
    setDeviceType(device);
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: storyboard.headline,
          text: `Check out this ${storyboard.templateType} guide`,
          url: window.location.href
        });
      } catch (error) {
        logger.error('Error sharing', error);
      }
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <Card className={cn('w-full', className)}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              SIMPI Preview
            </CardTitle>
            <CardDescription>
              {storyboard.headline} • Step {currentStepIndex + 1} of {storyboard.steps.length}
            </CardDescription>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Device Selection */}
            <div className="flex items-center gap-1 mr-2">
              <Button
                variant={deviceType === 'desktop' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleDeviceChange('desktop')}
                className="h-8 w-8 p-0"
              >
                <Monitor className="h-4 w-4" />
              </Button>
              <Button
                variant={deviceType === 'tablet' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleDeviceChange('tablet')}
                className="h-8 w-8 p-0"
              >
                <Tablet className="h-4 w-4" />
              </Button>
              <Button
                variant={deviceType === 'mobile' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleDeviceChange('mobile')}
                className="h-8 w-8 p-0"
              >
                <Smartphone className="h-4 w-4" />
              </Button>
            </div>

            {/* Fullscreen Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleFullscreen}
              className="h-8 w-8 p-0"
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>

            {/* Share */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              className="h-8 w-8 p-0"
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {/* Preview Area */}
          <div 
            ref={previewRef}
            className={cn(
              'mx-auto bg-gray-100 rounded-lg p-4 transition-all duration-300',
              isFullscreen && 'fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-8'
            )}
            style={{
              maxWidth: deviceType === 'desktop' ? '100%' : deviceSizes[deviceType].width,
              minHeight: deviceType === 'desktop' ? '400px' : deviceSizes[deviceType].height
            }}
          >
            <div className={cn(
              'w-full h-full bg-white rounded-lg shadow-lg overflow-hidden',
              deviceType !== 'desktop' && 'max-w-full max-h-full'
            )}>
              {currentStep && (
                <SimpiStepPreview
                  step={currentStep}
                  isActive={true}
                  onPlay={handlePlayStep}
                  onPause={handlePauseStep}
                  isPlaying={isPlaying}
                  isMuted={isMuted}
                  onToggleMute={() => setIsMuted(!isMuted)}
                />
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevStep}
                disabled={isFirstStep}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextStep}
                disabled={isLastStep}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRestart}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Restart
              </Button>
              
              <Button
                variant="default"
                size="sm"
                onClick={isPlaying ? handlePauseStep : handlePlayStep}
              >
                {isPlaying ? (
                  <>
                    <Pause className="h-4 w-4 mr-2" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Play
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Step Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Progress</span>
              <span>{currentStepIndex + 1} / {storyboard.steps.length}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStepIndex + 1) / storyboard.steps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Export Options */}
          <div className="flex items-center justify-center gap-2 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport('json')}
            >
              <Download className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport('pdf')}
            >
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport('html')}
            >
              <Download className="h-4 w-4 mr-2" />
              Export HTML
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};